import { websiteEventContext } from '@mts/shared/websiteAnalytics';
import { canTrackVisitorEvent } from './visitorTracking.js';
import { getApiBaseUrl, getTenantSlugFromHostname } from './api';

function buildHeaders(extra = {}) {
  const headers = {
    ...extra,
  };

  const tenantSlug = getTenantSlugFromHostname();
  if (tenantSlug) {
    headers['X-Tenant-Slug'] = tenantSlug;
  }

  return headers;
}

async function requestJson(path, options = {}) {
  const baseUrl = getApiBaseUrl();
  const method = String(options.method || 'GET').toUpperCase();
  const response = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    ...options,
    headers: buildHeaders({
      ...(method !== 'GET' && method !== 'HEAD' && options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    }),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(payload?.message || payload?.error || `HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return payload;
}

export function normalizeOffer(offer = {}) {
  return {
    ...offer,
    title: offer?.title || '',
    shortDescription: offer?.shortDescription || '',
    description: offer?.description || '',
    canEdit: Boolean(offer?.canEdit),
    offerType: offer?.offerType || 'OTHER',
    promoCode: offer?.promoCode || '',
    redemptionMethod: offer?.redemptionMethod || 'NO_REDEMPTION',
    captureMode: offer?.captureMode || 'NONE',
    finePrintPresetTermIds: Array.isArray(offer?.finePrintPresetTermIds) ? offer.finePrintPresetTermIds : [],
    finePrintPresetTerms: Array.isArray(offer?.finePrintPresetTerms) ? offer.finePrintPresetTerms : [],
    status: offer?.status || 'DRAFT',
    computedStatus: offer?.computedStatus || offer?.status || 'DRAFT',
    placements: Array.isArray(offer?.placements) ? offer.placements : [],
  };
}

export async function getHomepageOffers() {
  const payload = await requestJson('/api/public/homepage/offers', { method: 'GET' });
  return (payload?.offers || []).map(normalizeOffer);
}

export async function getOffersPage(filters = {}) {
  const params = new URLSearchParams();

  if (filters?.listingId) {
    params.set('listingId', String(filters.listingId));
  }
  if (filters?.categorySlug) params.set('categorySlug', String(filters.categorySlug));
  if (filters?.placementType) params.set('placementType', String(filters.placementType));

  const query = params.toString();
  const payload = await requestJson(`/api/offers${query ? `?${query}` : ''}`, { method: 'GET' });
  const offers = Array.isArray(payload?.offers) ? payload.offers : [];
  return { offers: offers.map(normalizeOffer), category: payload?.category || null };
}

export async function getOffers(filters = {}) {
  return (await getOffersPage(filters)).offers;
}

export async function getListingOffers(listingId) {
  const payload = await requestJson(`/api/listings/${encodeURIComponent(String(listingId || ''))}/offers`, { method: 'GET' });
  const offers = Array.isArray(payload?.offers) ? payload.offers : [];
  return offers.map(normalizeOffer);
}

export async function getOffer(offerId) {
  const encodedOfferId = encodeURIComponent(String(offerId || ''));
  const payload = await requestJson(`/api/offers/${encodedOfferId}`, { method: 'GET' });
  return normalizeOffer(payload?.offer || payload);
}

export async function trackOfferEvent(offerId, eventType, { placementId = null, metadataJson = {} } = {}) {
  if (!canTrackVisitorEvent('offer.impression')) return { recorded: false };
  const context = websiteEventContext({ pageUrl: window.location.href, referrer: document.referrer || null, canonicalOrigin: window.location.origin, analyticsConsent: true });
  if (!context) return { recorded: false };
  const safeMetadata = Object.fromEntries(Object.entries(metadataJson || {}).filter(([key, value]) => ['surface', 'action'].includes(key) && typeof value === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(value)));
  return requestJson(`/api/offers/${encodeURIComponent(String(offerId || ''))}/events`, {
    method: 'POST',
    keepalive: true,
    body: JSON.stringify({ eventType, placementId, metadataJson: { ...safeMetadata, websiteAnalytics: { version: 1, analyticsConsent: true, taggedCampaign: context.channel === 'tagged_campaign', paidTag: context.channel === 'paid_tag' } }, pageUrl: `${window.location.origin}${context.path}`, referrer: context.referrerHost ? `https://${context.referrerHost}/` : null }),
  });
}

export async function saveOffer(offerId, { placementId = null } = {}) {
  const payload = await requestJson(`/api/offers/${encodeURIComponent(String(offerId || ''))}/save`, { method: 'POST', body: JSON.stringify({ placementId }) });
  if (payload?.recorded !== true) throw new Error('Interest could not be recorded. Please try again.');
  return normalizeOffer(payload?.offer || payload);
}

export async function updateOffer(offerId, data) {
  const payload = await requestJson(`/api/offers/${encodeURIComponent(String(offerId || ''))}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return normalizeOffer(payload?.offer || payload);
}

export async function uploadOfferImage(offerId, file) {
  if (!offerId || !file) throw new Error('Offer and image are required.');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('ownerType', 'OFFER');
  formData.append('ownerId', String(offerId));
  formData.append('collectionKey', 'offer.cover');
  formData.append('visibility', 'PUBLIC');
  formData.append('tags', JSON.stringify(['offer', 'cover', 'upload']));

  const response = await fetch(`${getApiBaseUrl()}/api/media/assets`, {
    method: 'POST',
    credentials: 'include',
    headers: buildHeaders(),
    body: formData,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.message || payload?.error || `HTTP ${response.status}`);
  return payload?.asset || payload;
}

export async function searchOfferPexelsPhotos(tenantId, listingId, query) {
  if (!tenantId || !listingId || !String(query || '').trim()) {
    throw new Error('Tenant, listing, and search query are required.');
  }
  const params = new URLSearchParams({
    listingId: String(listingId),
    query: String(query).trim(),
    page: '1',
    perPage: '15',
    orientation: 'square',
  });
  return requestJson(`/api/tenants/${encodeURIComponent(tenantId)}/pexels/photos/search?${params.toString()}`);
}

export async function importPexelsOfferImage(tenantId, offerId, photo) {
  if (!tenantId || !offerId || !photo?.pexelsPhotoId) {
    throw new Error('Offer and Pexels photo are required.');
  }
  const payload = await requestJson(`/api/tenants/${encodeURIComponent(tenantId)}/media/import-pexels-photo`, {
    method: 'POST',
    body: JSON.stringify({
      photoId: photo.pexelsPhotoId,
      ownerType: 'OFFER',
      ownerId: String(offerId),
      collectionKey: 'offer.cover',
      visibility: 'PUBLIC',
      altText: photo.alt || 'Offer image',
      tags: ['offer', 'cover', 'pexels'],
    }),
  });
  return payload?.asset || payload;
}

export async function fetchOfferGooglePhotos(tenantId, listingId, googlePlaceId) {
  if (!tenantId || !listingId || !googlePlaceId) {
    throw new Error('This listing is not linked to Google Photos.');
  }
  const params = new URLSearchParams({ listingId: String(listingId) });
  return requestJson(
    `/api/tenants/${encodeURIComponent(tenantId)}/places/${encodeURIComponent(googlePlaceId)}/photos?${params.toString()}`,
  );
}

export async function importGoogleOfferImage(tenantId, listingId, photoName) {
  if (!tenantId || !listingId || !photoName) {
    throw new Error('Listing and Google photo are required.');
  }
  const payload = await requestJson(
    `/api/tenants/${encodeURIComponent(tenantId)}/listings/${encodeURIComponent(listingId)}/gallery/import-google-photo`,
    {
      method: 'POST',
      body: JSON.stringify({ photoName }),
    },
  );
  return payload?.asset || payload;
}

export async function requestOffer(offerId, data = {}) {
  const payload = await requestJson(`/api/offers/${encodeURIComponent(String(offerId || ''))}/request`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
  return {
    offer: normalizeOffer(payload?.offer || payload),
    unlocked: Boolean(payload?.request?.id),
    request: payload?.request || null,
  };
}

export async function reportOffer(offerId, data = {}) {
  return requestJson(`/api/offers/${encodeURIComponent(String(offerId || ''))}/report`, { method: 'POST', body: JSON.stringify(data) });
}
